import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP, formatDate, todayStr } from '@/lib/utils';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, CreditCard, MessageSquare, Settings, Pencil, Trash2, Check, X, FileText, Upload, Download, Plus, File } from 'lucide-react';
import { MoneyInput } from '@/components/MoneyInput';
import Autocomplete from '@/components/Autocomplete';
import { useAuth } from '@/contexts/AuthContext';

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDeeplook = user?.role === 'deeplook';
  const [deal, setDeal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [comments, setComments] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [payForm, setPayForm] = useState({ amount: '', payment_method: 'transferencia', date: todayStr(), payment_type: 'apartment', paid_to: '' });
  const [receiptForm, setReceiptForm] = useState({ amount: '', received_by: '', payment_method: 'transferencia', date: todayStr() });
  const [disbForm, setDisbForm] = useState({ amount: '', paid_from: '', paid_to: '', payment_method: 'transferencia', date: todayStr() });
  const [commentText, setCommentText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPayData, setEditPayData] = useState({});
  const [sectors, setSectors] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [newOtroSiCount, setNewOtroSiCount] = useState(0);
  const [customDocs, setCustomDocs] = useState([]);
  const [newCustomName, setNewCustomName] = useState('');
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [linkedLead, setLinkedLead] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [leadPick, setLeadPick] = useState('');
  const [linking, setLinking] = useState(false);

  const loadLinkedLead = useCallback(async () => {
    try { const r = await api.get(`/deals/${id}/linked-lead`); setLinkedLead(r.lead || null); } catch {}
  }, [id]);

  const linkLead = async (item) => {
    setLinking(true);
    try {
      const r = await api.post(`/deals/${id}/link-lead`, { lead_id: item.lead_id || item.id });
      setLinkedLead(r.lead);
      setLeadPick('');
      toast.success(`Vinculado a ${r.lead.full_name} — el lead quedó en Cerrado`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo vincular'); }
    setLinking(false);
  };

  const unlinkLead = async () => {
    if (!window.confirm('¿Desvincular este lead del negocio? El lead se mantiene en el CRM.')) return;
    setLinking(true);
    try {
      await api.del(`/deals/${id}/link-lead`);
      setLinkedLead(null);
      toast.success('Lead desvinculado');
    } catch { toast.error('No se pudo desvincular'); }
    setLinking(false);
  };

  const uploadDocument = async (file, docType, name) => {
    setUploading(name);
    try {
      // Convert file to base64 to avoid proxy body size limits on multipart
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/deals/${id}/documents/b64`, {
        file_data: base64Data,
        filename: file.name,
        doc_type: docType,
        name: name
      });
      toast.success(`${name} subido correctamente`);
      loadDocs();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Error al subir archivo';
      toast.error(msg);
    }
    setUploading(null);
  };

  const downloadDoc = (docId, filename) => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${apiUrl}/api/documents/${docId}/download`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url); })
      .catch(() => toast.error('Error al descargar'));
  };

  const deleteDoc = async (docId, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try { await api.del(`/documents/${docId}`); toast.success('Documento eliminado'); loadDocs(); }
    catch { toast.error('Error al eliminar'); }
  };

  const loadDocs = useCallback(async () => {
    try { const d = await api.get(`/deals/${id}/documents`); setDocuments(d); } catch {}
  }, [id]);

  const deletePayment = async (paymentId) => {
    if (!window.confirm('¿Eliminar este pago? Esta accion no se puede deshacer.')) return;
    try {
      await api.del(`/payments/delete/${paymentId}`);
      toast.success('Pago eliminado');
      load();
    } catch { toast.error('Error al eliminar pago'); }
  };

  const startEditPayment = (p) => {
    setEditingPayment(p.id);
    setEditPayData({ amount: p.amount, date: p.date, payment_method: p.payment_method, paid_to: p.paid_to || '', paid_from: p.paid_from || '', received_by: p.received_by || '' });
  };

  const saveEditPayment = async (paymentId) => {
    try {
      await api.put(`/payments/edit/${paymentId}`, editPayData);
      toast.success('Pago actualizado');
      setEditingPayment(null);
      load();
    } catch { toast.error('Error al actualizar pago'); }
  };

  const load = useCallback(async () => {
    try {
      const [d, p, c, u, s] = await Promise.all([api.get(`/deals/${id}`), api.get(`/payments/${id}`), api.get(`/comments/${id}`), api.get('/users'), api.get('/sectors')]);
      setDeal(d);
      setEditForm(d);
      setPayments(p);
      setComments(c);
      setBrokers(u);
      setSectors(s);
    } catch { toast.error('Error al cargar negocio'); }
  }, [id]);

  useEffect(() => { load(); loadDocs(); loadLinkedLead(); }, [load, loadDocs, loadLinkedLead]);
  useEffect(() => { api.get('/crm/all-clients').then(setDirectory).catch(() => {}); }, []);

  const updateField = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  const saveDeal = async () => {
    setSaving(true);
    try {
      const update = {};
      const editableFields = ['next_step', 'next_step_date', 'next_payment_value', 'next_payment_date', 'closing_value', 'published_value', 'closing_date',
        'commission_pct', 'agency_split', 'client_broker_split', 'owner_broker_split', 'external_split', 'is_external_broker',
        'external_broker_name', 'external_broker_handles', 'client_agent_id', 'client_agent_name', 'owner_agent_id', 'owner_agent_name',
        'client_name', 'owner_name', 'property_address', 'deal_type', 'sector',
        'deeplook_linked', 'deeplook_commission_pct'];
      editableFields.forEach(f => { if (editForm[f] !== deal[f]) update[f] = editForm[f]; });
      if (Object.keys(update).length > 0) {
        await api.put(`/deals/${id}`, update);
        toast.success('Negocio actualizado');
        load();
      } else {
        toast.info('Sin cambios');
      }
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const addPayment = async () => {
    if (!payForm.amount) return toast.error('Ingresa un monto');
    setSaving(true);
    try {
      await api.post('/payments', { ...payForm, deal_id: id, amount: parseFloat(payForm.amount) });
      toast.success('Pago registrado');
      setPayForm({ amount: '', payment_method: 'transferencia', date: todayStr(), payment_type: 'apartment', paid_to: '' });
      load();
    } catch { toast.error('Error al registrar pago'); }
    setSaving(false);
  };

  const addCommissionReceipt = async () => {
    if (!receiptForm.amount || !receiptForm.received_by) return toast.error('Completa monto y quien recibio');
    setSaving(true);
    try {
      await api.post('/payments', { deal_id: id, amount: parseFloat(receiptForm.amount), payment_method: receiptForm.payment_method,
        date: receiptForm.date, payment_type: 'commission_receipt', received_by: receiptForm.received_by });
      toast.success('Comision registrada');
      setReceiptForm({ amount: '', received_by: '', payment_method: 'transferencia', date: todayStr() });
      load();
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const addDisbursement = async () => {
    if (!disbForm.amount || !disbForm.paid_from || !disbForm.paid_to) return toast.error('Completa todos los campos');
    setSaving(true);
    try {
      await api.post('/payments', { deal_id: id, amount: parseFloat(disbForm.amount), payment_method: disbForm.payment_method,
        date: disbForm.date, payment_type: 'disbursement', paid_from: disbForm.paid_from, paid_to: disbForm.paid_to });
      toast.success('Dispersion registrada');
      setDisbForm({ amount: '', paid_from: '', paid_to: '', payment_method: 'transferencia', date: todayStr() });
      load();
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post('/comments', { deal_id: id, text: commentText });
      setCommentText('');
      toast.success('Comentario agregado');
      load();
    } catch { toast.error('Error'); }
  };

  const deleteDeal = async () => {
    if (!window.confirm('¿Eliminar este negocio y todos sus pagos? Esta accion no se puede deshacer.')) return;
    try {
      await api.del(`/deals/${id}`);
      toast.success('Negocio eliminado');
      navigate('/negocios');
    } catch { toast.error('Error al eliminar negocio'); }
  };

  if (!deal) return <div className="flex items-center justify-center h-64"><div className="animate-pulse font-outfit text-lg text-white/50">Cargando negocio...</div></div>;

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";

  return (
    <div data-testid="deal-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/negocios')} className="text-white/50 hover:text-white transition-colors"><ArrowLeft size={20} strokeWidth={1.5} /></button>
          <div>
            <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Gestion Rapida</h1>
            <p className="text-sm text-white/50">{deal.property_address} — {deal.client_name}</p>
          </div>
        </div>
        <button onClick={deleteDeal} data-testid="delete-deal-btn" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-sm border border-red-500/20">
          <Trash2 size={14} /> <span className="hidden sm:inline">Eliminar</span>
        </button>
      </div>

      {/* Summary Banner */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-5 flex flex-wrap gap-4 lg:gap-6 items-center">
        <Badge className={deal.deal_type === 'venta' ? 'bg-[#A38560]/10 text-[#A38560] border-0' : 'bg-[#6BCB77]/10 text-[#6BCB77] border-0'}>{deal.deal_type === 'venta' ? 'Venta' : 'Arriendo'}</Badge>
        <div><p className="text-xs text-white/50">Valor Cierre</p><p className="font-outfit text-xl font-bold text-white tabular-nums">{formatCOP(deal.closing_value)}</p></div>
        <div><p className="text-xs text-white/50">Comision SELECTO</p><p className="font-outfit text-xl font-bold text-[#A38560] tabular-nums">{formatCOP(deal.agency_commission)}</p></div>
        <div><p className="text-xs text-white/50">Pagado</p><p className="font-outfit text-lg font-medium text-white tabular-nums">{formatCOP(deal.paid_to_date)}</p></div>
        <div><p className="text-xs text-white/50">Com. Pagada</p><p className="font-outfit text-lg font-medium text-[#6BCB77] tabular-nums">{formatCOP(deal.commission_paid_to_date)}</p></div>
      </div>

      <Tabs defaultValue="datos">
        <TabsList className="bg-[#0A1F1A] border border-white/5 h-auto p-1 rounded-xl flex flex-wrap gap-1">
          <TabsTrigger value="datos" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-sm rounded-lg flex-1 min-w-[80px] py-2.5"><Settings size={14} /> Datos</TabsTrigger>
          <TabsTrigger value="pagos" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-sm rounded-lg flex-1 min-w-[80px] py-2.5"><CreditCard size={14} /> Pagos</TabsTrigger>
          <TabsTrigger value="comisiones" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-sm rounded-lg flex-1 min-w-[80px] py-2.5" data-testid="tab-comisiones"><CreditCard size={14} /> Comisiones</TabsTrigger>
          <TabsTrigger value="bitacora" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-sm rounded-lg flex-1 min-w-[80px] py-2.5"><MessageSquare size={14} /> Bitacora</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-sm rounded-lg flex-1 min-w-[80px] py-2.5"><FileText size={14} /> Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="datos">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4" data-testid="linked-lead-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-outfit text-lg font-medium text-white">Lead del CRM vinculado</h3>
                <p className="text-[11px] text-white/40">Asocia este negocio a un lead existente sin volver a crearlo. Al vincular, el lead pasa a Cerrado.</p>
              </div>
              {linkedLead && (
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/crm/${linkedLead.id}`)} data-testid="open-linked-lead-btn"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#A38560]/10 text-[#A38560] hover:bg-[#A38560]/20 text-sm border border-[#A38560]/20 transition-colors">
                    <ArrowLeft size={14} className="rotate-180" /> Ver en CRM
                  </button>
                  {!isDeeplook && (
                    <button onClick={unlinkLead} disabled={linking} data-testid="unlink-lead-btn"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-white/50 hover:bg-white/10 text-sm border border-white/10 transition-colors disabled:opacity-50">
                      <X size={14} /> Desvincular
                    </button>
                  )}
                </div>
              )}
            </div>
            {linkedLead ? (
              <div className="flex items-center gap-3 flex-wrap p-3 rounded-xl bg-[#16302B] border border-white/5" data-testid="linked-lead-info">
                <span className="w-2 h-2 rounded-full bg-[#6BCB77]" />
                <span className="text-white font-medium">{linkedLead.full_name}</span>
                {linkedLead.phone && <span className="text-xs text-white/40">{linkedLead.phone}</span>}
                <Badge className="bg-[#6BCB77]/10 text-[#6BCB77] border-0 text-[10px]">{linkedLead.stage === 'cerrado' ? 'Cerrado' : linkedLead.stage}</Badge>
                {linkedLead.source && <span className="text-xs text-white/30">Fuente: {linkedLead.source}</span>}
                {linkedLead.assigned_broker_name && <span className="text-xs text-white/30">Broker: {linkedLead.assigned_broker_name}</span>}
              </div>
            ) : isDeeplook ? (
              <p className="text-sm text-white/40">Este negocio no tiene un lead vinculado.</p>
            ) : (
              <div className="max-w-md" data-testid="link-lead-search">
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Buscar lead o cliente</Label>
                <Autocomplete items={directory} value={leadPick} secondaryField="phone" showCreateNew={false}
                  onChange={setLeadPick} onSelect={linkLead} placeholder="Escribe el nombre o teléfono..." />
              </div>
            )}
          </div>
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            {isDeeplook ? (
              <div>
                <h3 className="font-outfit text-lg font-medium text-white mb-3">Informacion del Negocio</h3>
                <p className="text-xs text-white/30 mb-4">Solo lectura — contacta al administrador para modificaciones</p>
              </div>
            ) : (
              <>
            <h3 className="font-outfit text-lg font-medium text-white">Modificar Datos</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor de Cierre</Label>
                <MoneyInput value={editForm.closing_value || ''} onChange={v => updateField('closing_value', v)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor Publicado</Label>
                <MoneyInput value={editForm.published_value || ''} onChange={v => updateField('published_value', v)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha de Cierre</Label>
                <Input type="date" value={editForm.closing_date || ''} onChange={e => updateField('closing_date', e.target.value)} className={inputCls} /></div>
            </div>
            {/* Basic deal info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Cliente</Label>
                <Input value={editForm.client_name || ''} onChange={e => updateField('client_name', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Propietario</Label>
                <Input value={editForm.owner_name || ''} onChange={e => updateField('owner_name', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Direccion</Label>
                <Input value={editForm.property_address || ''} onChange={e => updateField('property_address', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo</Label>
                <Select value={editForm.deal_type || 'venta'} onValueChange={v => updateField('deal_type', v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentCls}><SelectItem value="venta">Venta</SelectItem><SelectItem value="arriendo">Arriendo</SelectItem></SelectContent>
                </Select></div>
            </div>
            {/* Financial */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor Cierre</Label>
                <MoneyInput value={editForm.closing_value || ''} onChange={v => updateField('closing_value', v)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Valor Publicado</Label>
                <MoneyInput value={editForm.published_value || ''} onChange={v => updateField('published_value', v)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha Cierre</Label>
                <Input type="date" value={editForm.closing_date || ''} onChange={e => updateField('closing_date', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Sector</Label>
                <Select value={editForm.sector || ''} onValueChange={v => updateField('sector', v)}>
                  <SelectTrigger className={inputCls}><SelectValue placeholder="Seleccionar sector" /></SelectTrigger>
                  <SelectContent className={selectContentCls}>{sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {/* Agents */}
            <div className="border-t border-white/10 pt-4">
              <h4 className="text-sm font-medium text-white/70 mb-3">Agentes</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Broker Cliente</Label>
                  <Select value={editForm.client_agent_id || ''} onValueChange={v => { const b = brokers.find(x => x.id === v); updateField('client_agent_id', v); updateField('client_agent_name', b?.name || ''); }}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Broker Propietario</Label>
                  <Select value={editForm.owner_agent_id || ''} onValueChange={v => { const b = brokers.find(x => x.id === v); updateField('owner_agent_id', v); updateField('owner_agent_name', b?.name || ''); }}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="flex items-center gap-3 mt-3 p-3 bg-[#16302B] rounded-xl border border-white/5">
                <input type="checkbox" checked={editForm.is_external_broker || false} onChange={e => updateField('is_external_broker', e.target.checked)} className="w-4 h-4 rounded border-white/30 accent-[#A38560]" />
                <p className="text-sm text-white">Broker externo</p>
                {editForm.is_external_broker && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Input value={editForm.external_broker_name || ''} onChange={e => updateField('external_broker_name', e.target.value)} placeholder="Nombre" className={`${inputCls} w-40`} />
                    <Select value={editForm.external_broker_handles || 'client'} onValueChange={v => updateField('external_broker_handles', v)}>
                      <SelectTrigger className={`${inputCls} w-32`}><SelectValue /></SelectTrigger>
                      <SelectContent className={selectContentCls}><SelectItem value="client">Cliente</SelectItem><SelectItem value="owner">Propietario</SelectItem></SelectContent>
                    </Select>
                    <div className="flex items-center gap-1"><Label className="text-white/40 text-xs">Split %</Label>
                    <Input type="number" value={editForm.external_split ?? 50} onChange={e => updateField('external_split', parseFloat(e.target.value) || 0)} className={`${inputCls} w-16`} /></div>
                  </div>
                )}
              </div>
            </div>
            {/* Steps & Payments */}
            <div className="border-t border-white/10 pt-4">
              <h4 className="text-sm font-medium text-white/70 mb-3">Seguimiento</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Siguiente Paso</Label>
                <Input value={editForm.next_step || ''} onChange={e => updateField('next_step', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha Sig. Paso</Label>
                <Input type="date" value={editForm.next_step_date || ''} onChange={e => updateField('next_step_date', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Sig. Pago Valor</Label>
                <MoneyInput value={editForm.next_payment_value || ''} onChange={v => updateField('next_payment_value', v)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha Sig. Pago</Label>
                <Input type="date" value={editForm.next_payment_date || ''} onChange={e => updateField('next_payment_date', e.target.value)} className={inputCls} /></div>
            </div>
            </div>
            {/* Commission splits */}
            <div className="border-t border-white/10 pt-4">
              <h4 className="text-sm font-medium text-white/70 mb-3">Comisiones y Splits</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Comision</Label>
                  <Input type="number" step="0.1" value={editForm.commission_pct ?? ''} onChange={e => updateField('commission_pct', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Agencia</Label>
                  <Input type="number" step="1" value={editForm.agency_split ?? ''} onChange={e => updateField('agency_split', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Broker Cliente</Label>
                  <Input type="number" step="1" value={editForm.client_broker_split ?? ''} onChange={e => updateField('client_broker_split', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">% Broker Propietario</Label>
                  <Input type="number" step="1" value={editForm.owner_broker_split ?? ''} onChange={e => updateField('owner_broker_split', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
              </div>
              {/* Deeplook toggle */}
              <div className="flex items-center gap-3 mt-3 p-3 bg-purple-500/5 rounded-xl border border-purple-500/10">
                <input type="checkbox" checked={editForm.deeplook_linked || false} onChange={e => updateField('deeplook_linked', e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 accent-purple-500" data-testid="edit-deeplook-check" />
                <div className="flex-1">
                  <p className="text-sm text-white">Cliente / Propietario llego por Deeplook</p>
                </div>
                {editForm.deeplook_linked && (
                  <div className="flex items-center gap-2">
                    <Label className="text-purple-400/80 text-xs">% Deeplook</Label>
                    <Input type="number" step="1" value={editForm.deeplook_commission_pct ?? 20} onChange={e => updateField('deeplook_commission_pct', parseFloat(e.target.value) || 0)} className={`${inputCls} w-20`} />
                  </div>
                )}
              </div>
              {deal.total_commission > 0 && (
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-white/50">
                  <span>Comision total: <b className="text-white">{formatCOP(deal.total_commission)}</b></span>
                  <span>Agencia: <b className="text-[#A38560]">{formatCOP(deal.agency_commission)}</b></span>
                  <span>Broker cliente: <b className="text-white">{formatCOP(deal.client_broker_commission)}</b></span>
                  <span>Broker propietario: <b className="text-white">{formatCOP(deal.owner_broker_commission)}</b></span>
                  {deal.deeplook_commission > 0 && <span>Deeplook: <b className="text-purple-400">{formatCOP(deal.deeplook_commission)}</b></span>}
                </div>
              )}
            </div>
            <button onClick={saveDeal} disabled={saving} data-testid="save-deal-changes-btn"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">
              <Save size={16} /> Guardar Cambios
            </button>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pagos">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h3 className="font-outfit text-lg font-medium text-white">Registrar Pago del Apartamento</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Monto</Label>
                <MoneyInput data-testid="payment-amount" value={payForm.amount} onChange={v => setPayForm({ ...payForm, amount: v })} placeholder="0" className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Forma de Pago</Label>
                <Select value={payForm.payment_method} onValueChange={v => setPayForm({ ...payForm, payment_method: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentCls}><SelectItem value="transferencia">Transferencia</SelectItem><SelectItem value="efectivo">Efectivo</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha</Label>
                <Input type="date" value={payForm.date} onChange={e => setPayForm({ ...payForm, date: e.target.value })} className={inputCls} /></div>
            </div>
            <button onClick={() => { setPayForm({ ...payForm, payment_type: 'apartment' }); addPayment(); }} disabled={saving} data-testid="register-payment-btn"
              className="w-full sm:w-auto bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">Registrar Pago</button>
            {payments.filter(p => p.payment_type === 'apartment').length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-white">Historial de Pagos</h4>
                {payments.filter(p => p.payment_type === 'apartment').map(p => (
                  <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border border-white/5 gap-1">
                    {editingPayment === p.id ? (
                      <div className="flex flex-wrap items-center gap-2 w-full">
                        <Input type="number" value={editPayData.amount} onChange={e => setEditPayData({...editPayData, amount: parseFloat(e.target.value) || 0})} className={`${inputCls} w-32`} />
                        <Input type="date" value={editPayData.date} onChange={e => setEditPayData({...editPayData, date: e.target.value})} className={`${inputCls} w-40`} />
                        <button onClick={() => saveEditPayment(p.id)} className="p-1.5 bg-[#6BCB77]/20 text-[#6BCB77] rounded-lg"><Check size={14} /></button>
                        <button onClick={() => setEditingPayment(null)} className="p-1.5 bg-red-400/10 text-red-400 rounded-lg"><X size={14} /></button>
                      </div>
                    ) : (
                      <>
                        <div><span className="font-medium text-white tabular-nums">{formatCOP(p.amount)}</span><span className="text-white/50"> — {p.payment_method}</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50">{formatDate(p.date)}</span>
                          <button onClick={() => startEditPayment(p)} className="p-1 text-white/30 hover:text-[#A38560] transition-colors"><Pencil size={13} /></button>
                          <button onClick={() => deletePayment(p.id)} className="p-1 text-white/30 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="comisiones">
          <div className="space-y-6 mt-4">
            {/* Commission Summary */}
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
              <h3 className="font-outfit text-lg font-medium text-white mb-3">Resumen de Comisiones</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 p-3 lg:p-4 bg-[#16302B] rounded-xl border border-white/5">
                <div><p className="text-xs text-white/50">Com. Total</p><p className="font-outfit font-bold text-[#A38560] tabular-nums">{formatCOP(deal.total_commission)}</p></div>
                <div><p className="text-xs text-white/50">SELECTO (Agencia)</p><p className="font-outfit font-bold text-white tabular-nums">{formatCOP(deal.agency_commission)}</p></div>
                <div><p className="text-xs text-white/50">Broker Cliente{deal.client_agent_name ? ` (${deal.client_agent_name})` : ''}</p><p className="font-outfit font-bold text-[#6BCB77] tabular-nums">{formatCOP(deal.client_broker_commission)}</p></div>
                <div><p className="text-xs text-white/50">Broker Propietario{deal.owner_agent_name ? ` (${deal.owner_agent_name})` : ''}</p><p className="font-outfit font-bold text-[#6BCB77] tabular-nums">{formatCOP(deal.owner_broker_commission)}</p></div>
                {deal.deeplook_linked && <div><p className="text-xs text-purple-400/80">Deeplook ({deal.deeplook_commission_pct || 20}%)</p><p className="font-outfit font-bold text-purple-400 tabular-nums">{formatCOP(deal.deeplook_commission)}</p></div>}
              </div>
            </div>

            {/* Block 1: Commission Receipt from Owner */}
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
              <div>
                <h3 className="font-outfit text-lg font-medium text-white">Registro de Comision del Propietario</h3>
                <p className="text-xs text-white/50 mt-1">Registrar cuando el propietario paga la comision a un broker</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Monto</Label>
                  <MoneyInput value={receiptForm.amount} onChange={v => setReceiptForm({...receiptForm, amount: v})} placeholder="0" className={inputCls} /></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Recibida por</Label>
                  <Select value={receiptForm.received_by} onValueChange={v => setReceiptForm({...receiptForm, received_by: v})}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Quien recibio" /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {brokers.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Metodo</Label>
                  <Select value={receiptForm.payment_method} onValueChange={v => setReceiptForm({...receiptForm, payment_method: v})}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className={selectContentCls}><SelectItem value="transferencia">Transferencia</SelectItem><SelectItem value="efectivo">Efectivo</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha</Label>
                  <Input type="date" value={receiptForm.date} onChange={e => setReceiptForm({...receiptForm, date: e.target.value})} className={inputCls} /></div>
              </div>
              <button onClick={addCommissionReceipt} disabled={saving} data-testid="register-receipt-btn"
                className="w-full sm:w-auto bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">Registrar Comision</button>
              {payments.filter(p => p.payment_type === 'commission_receipt').length > 0 && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                  <h4 className="text-sm font-medium text-white">Historial de Comisiones Recibidas</h4>
                  {payments.filter(p => p.payment_type === 'commission_receipt').map(p => (
                    <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border border-white/5 gap-1">
                      {editingPayment === p.id ? (
                        <div className="flex flex-wrap items-center gap-2 w-full">
                          <MoneyInput value={editPayData.amount} onChange={v => setEditPayData({...editPayData, amount: v})} className={`${inputCls} w-32`} />
                          <Input type="date" value={editPayData.date} onChange={e => setEditPayData({...editPayData, date: e.target.value})} className={`${inputCls} w-40`} />
                          <button onClick={() => saveEditPayment(p.id)} className="p-1.5 bg-[#6BCB77]/20 text-[#6BCB77] rounded-lg"><Check size={14} /></button>
                          <button onClick={() => setEditingPayment(null)} className="p-1.5 bg-red-400/10 text-red-400 rounded-lg"><X size={14} /></button>
                        </div>
                      ) : (
                        <>
                          <div><span className="font-medium text-white tabular-nums">{formatCOP(p.amount)}</span><span className="text-white/50"> — recibida por </span><span className="font-medium text-[#A38560]">{p.received_by}</span><span className="text-white/50"> ({p.payment_method})</span></div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs sm:text-sm">{formatDate(p.date)}</span>
                            <button onClick={() => startEditPayment(p)} className="p-1 text-white/30 hover:text-[#A38560] transition-colors" data-testid={`edit-pay-${p.id}`}><Pencil size={13} /></button>
                            <button onClick={() => deletePayment(p.id)} className="p-1 text-white/30 hover:text-red-400 transition-colors" data-testid={`del-pay-${p.id}`}><Trash2 size={13} /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Block 2: Internal Disbursement */}
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
              <div>
                <h3 className="font-outfit text-lg font-medium text-white">Dispersion Interna</h3>
                <p className="text-xs text-white/50 mt-1">Registrar pagos entre brokers y SELECTO (el broker que recibio la comision paga a los demas)</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Monto</Label>
                  <MoneyInput value={disbForm.amount} onChange={v => setDisbForm({...disbForm, amount: v})} placeholder="0" className={inputCls} /></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">De (quien paga)</Label>
                  <Select value={disbForm.paid_from} onValueChange={v => setDisbForm({...disbForm, paid_from: v})}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Quien paga" /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {brokers.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Para (quien recibe)</Label>
                  <Select value={disbForm.paid_to} onValueChange={v => setDisbForm({...disbForm, paid_to: v})}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Quien recibe" /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="Agencia SELECTO">Agencia SELECTO</SelectItem>
                      <SelectItem value="Deeplook">Deeplook</SelectItem>
                      {brokers.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Metodo</Label>
                  <Select value={disbForm.payment_method} onValueChange={v => setDisbForm({...disbForm, payment_method: v})}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className={selectContentCls}><SelectItem value="transferencia">Transferencia</SelectItem><SelectItem value="efectivo">Efectivo</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha</Label>
                  <Input type="date" value={disbForm.date} onChange={e => setDisbForm({...disbForm, date: e.target.value})} className={inputCls} /></div>
              </div>
              <button onClick={addDisbursement} disabled={saving} data-testid="register-disbursement-btn"
                className="w-full sm:w-auto bg-[#16302B] hover:bg-[#1A3B32] text-white font-semibold px-6 py-2.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 border border-white/10">Registrar Dispersion</button>
              {payments.filter(p => p.payment_type === 'disbursement').length > 0 && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                  <h4 className="text-sm font-medium text-white">Historial de Dispersiones</h4>
                  {payments.filter(p => p.payment_type === 'disbursement').map(p => (
                    <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border border-white/5 gap-1">
                      {editingPayment === p.id ? (
                        <div className="flex flex-wrap items-center gap-2 w-full">
                          <MoneyInput value={editPayData.amount} onChange={v => setEditPayData({...editPayData, amount: v})} className={`${inputCls} w-32`} />
                          <Input type="date" value={editPayData.date} onChange={e => setEditPayData({...editPayData, date: e.target.value})} className={`${inputCls} w-40`} />
                          <button onClick={() => saveEditPayment(p.id)} className="p-1.5 bg-[#6BCB77]/20 text-[#6BCB77] rounded-lg"><Check size={14} /></button>
                          <button onClick={() => setEditingPayment(null)} className="p-1.5 bg-red-400/10 text-red-400 rounded-lg"><X size={14} /></button>
                        </div>
                      ) : (
                        <>
                          <div className="text-xs sm:text-sm"><span className="font-medium text-[#A38560]">{p.paid_from}</span><span className="text-white/50"> pago a </span><span className="font-medium text-white">{p.paid_to}</span><span className="text-white/50">: </span><span className="tabular-nums font-medium text-white">{formatCOP(p.amount)}</span><span className="text-white/50"> ({p.payment_method})</span></div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs">{formatDate(p.date)}</span>
                            <button onClick={() => startEditPayment(p)} className="p-1 text-white/30 hover:text-[#A38560] transition-colors"><Pencil size={13} /></button>
                            <button onClick={() => deletePayment(p.id)} className="p-1 text-white/30 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Legacy commission payments */}
            {payments.filter(p => p.payment_type === 'commission').length > 0 && (
              <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6">
                <h4 className="text-sm font-medium text-white mb-2">Pagos de Comision Anteriores</h4>
                {payments.filter(p => p.payment_type === 'commission').map(p => (
                  <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm mb-2 border border-white/5 gap-1">
                    {editingPayment === p.id ? (
                      <div className="flex flex-wrap items-center gap-2 w-full">
                        <Input type="number" value={editPayData.amount} onChange={e => setEditPayData({...editPayData, amount: parseFloat(e.target.value) || 0})} className={`${inputCls} w-32`} />
                        <Input type="date" value={editPayData.date} onChange={e => setEditPayData({...editPayData, date: e.target.value})} className={`${inputCls} w-40`} />
                        <button onClick={() => saveEditPayment(p.id)} className="p-1.5 bg-[#6BCB77]/20 text-[#6BCB77] rounded-lg"><Check size={14} /></button>
                        <button onClick={() => setEditingPayment(null)} className="p-1.5 bg-red-400/10 text-red-400 rounded-lg"><X size={14} /></button>
                      </div>
                    ) : (
                      <>
                        <div><span className="font-medium tabular-nums text-white">{formatCOP(p.amount)}</span><span className="text-white/50"> — {p.paid_to} ({p.payment_method})</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50">{formatDate(p.date)}</span>
                          <button onClick={() => startEditPayment(p)} className="p-1 text-white/30 hover:text-[#A38560] transition-colors"><Pencil size={13} /></button>
                          <button onClick={() => deletePayment(p.id)} className="p-1 text-white/30 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bitacora">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h3 className="font-outfit text-lg font-medium text-white">Bitacora / Comentarios</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Textarea data-testid="comment-input" value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Agregar comentario o evento..."
                className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] flex-1 min-h-[60px] rounded-xl" />
              <button onClick={addComment} data-testid="add-comment-btn"
                className="bg-[#16302B] hover:bg-[#1A3B32] text-white px-4 py-2.5 rounded-xl sm:self-end transition-colors border border-white/10">Enviar</button>
            </div>
            <div className="space-y-3 mt-4">
              {comments.map(c => (
                <div key={c.id} className="p-3 border-l-2 border-[#A38560] bg-[#16302B] rounded-r-xl">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">{c.created_by_name}</span>
                    <span className="text-xs text-white/50">{formatDate(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-white/80">{c.text}</p>
                </div>
              ))}
              {comments.length === 0 && <p className="text-sm text-white/50">No hay comentarios aun.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documentos">
          <div className="space-y-6 mt-4">
            {/* Contrato Firmado */}
            <DocSlot title="Contrato Firmado" docType="contrato" name="Contrato firmado"
              doc={documents.find(d => d.doc_type === 'contrato')}
              uploading={uploading} onUpload={uploadDocument} onDownload={downloadDoc} onDelete={deleteDoc} inputCls={inputCls} />

            {/* Otro Si */}
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-outfit text-lg font-medium text-white">Otro Si</h3>
                <button data-testid="add-otrosi-btn" onClick={() => setNewOtroSiCount(prev => prev + 1)}
                  className="flex items-center gap-1.5 text-sm text-[#A38560] hover:text-[#C9A96E] transition-colors"><Plus size={16} /> Agregar Otro Si</button>
              </div>
              {documents.filter(d => d.doc_type === 'otrosi').map((doc, i) => (
                <DocFileRow key={doc.id} doc={doc} index={i + 1} onDownload={downloadDoc} onDelete={deleteDoc} />
              ))}
              {Array.from({ length: newOtroSiCount }).map((_, i) => {
                const existingCount = documents.filter(d => d.doc_type === 'otrosi').length;
                const num = existingCount + i + 1;
                return <DocUploadRow key={`new-otrosi-${i}`} name={`Otro si ${num}`} docType="otrosi"
                  uploading={uploading} onUpload={uploadDocument} onDone={() => setNewOtroSiCount(prev => prev - 1)} inputCls={inputCls} />;
              })}
              {documents.filter(d => d.doc_type === 'otrosi').length === 0 && newOtroSiCount === 0 &&
                <p className="text-sm text-white/40">No hay documentos "Otro si" agregados.</p>}
            </div>

            {/* Documentos personalizados */}
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-outfit text-lg font-medium text-white">Otros Documentos</h3>
                {!showAddCustom ? (
                  <button data-testid="add-custom-doc-btn" onClick={() => setShowAddCustom(true)}
                    className="flex items-center gap-1.5 text-sm text-[#A38560] hover:text-[#C9A96E] transition-colors"><Plus size={16} /> Agregar Documento</button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input value={newCustomName} onChange={e => setNewCustomName(e.target.value)} placeholder="Nombre del documento..."
                      className={`${inputCls} w-48 h-9 text-sm`} onKeyDown={e => { if (e.key === 'Enter' && newCustomName.trim()) { setCustomDocs(prev => [...prev, newCustomName.trim()]); setNewCustomName(''); setShowAddCustom(false); }}} autoFocus />
                    <button onClick={() => { if (newCustomName.trim()) { setCustomDocs(prev => [...prev, newCustomName.trim()]); setNewCustomName(''); setShowAddCustom(false); }}}
                      className="h-9 px-3 rounded-xl bg-[#A38560] text-[#03110D] text-sm font-medium">Crear</button>
                    <button onClick={() => { setShowAddCustom(false); setNewCustomName(''); }}
                      className="h-9 px-2 rounded-xl bg-white/5 text-white/40 hover:text-white"><X size={16} /></button>
                  </div>
                )}
              </div>
              {documents.filter(d => d.doc_type === 'custom').map(doc => (
                <DocFileRow key={doc.id} doc={doc} onDownload={downloadDoc} onDelete={deleteDoc} />
              ))}
              {customDocs.map((name, i) => (
                <DocUploadRow key={`custom-${i}`} name={name} docType="custom"
                  uploading={uploading} onUpload={uploadDocument} onDone={() => setCustomDocs(prev => prev.filter((_, idx) => idx !== i))} inputCls={inputCls} />
              ))}
              {documents.filter(d => d.doc_type === 'custom').length === 0 && customDocs.length === 0 &&
                <p className="text-sm text-white/40">No hay documentos adicionales.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* Sub-components for document management */
function DocSlot({ title, docType, name, doc, uploading, onUpload, onDownload, onDelete, inputCls }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
      <h3 className="font-outfit text-lg font-medium text-white">{title}</h3>
      {doc ? (
        <div className="flex items-center justify-between p-3 bg-[#16302B] rounded-xl border border-white/5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#A38560]/10 flex items-center justify-center shrink-0"><FileText size={18} className="text-[#A38560]" /></div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{doc.original_filename}</p>
              <p className="text-xs text-white/40">{(doc.file_size / 1024).toFixed(0)} KB — {new Date(doc.created_at).toLocaleDateString('es-CO')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onDownload(doc.id, doc.original_filename)} className="p-2 text-white/40 hover:text-[#A38560] transition-colors" data-testid={`download-${docType}`}><Download size={16} /></button>
            <button onClick={() => onDelete(doc.id, doc.name)} className="p-2 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-${docType}`}><Trash2 size={16} /></button>
          </div>
        </div>
      ) : (
        <div>
          <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.webp"
            onChange={e => { if (e.target.files[0]) { onUpload(e.target.files[0], docType, name); e.target.value = ''; }}} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading === name} data-testid={`upload-${docType}`}
            className="w-full border-2 border-dashed border-white/10 hover:border-[#A38560]/30 rounded-xl p-6 flex flex-col items-center gap-2 transition-colors group disabled:opacity-50">
            {uploading === name ? <div className="animate-spin w-6 h-6 border-2 border-[#A38560] border-t-transparent rounded-full" /> :
              <Upload size={24} className="text-white/20 group-hover:text-[#A38560]/60" />}
            <span className="text-sm text-white/40 group-hover:text-white/60">{uploading === name ? 'Subiendo...' : 'Click para subir archivo'}</span>
            <span className="text-xs text-white/20">PDF, Word, Excel, Imagen (max 20MB)</span>
          </button>
        </div>
      )}
    </div>
  );
}

function DocFileRow({ doc, index, onDownload, onDelete }) {
  return (
    <div className="flex items-center justify-between p-3 bg-[#16302B] rounded-xl border border-white/5">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-[#A38560]/10 flex items-center justify-center shrink-0"><File size={16} className="text-[#A38560]" /></div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{doc.name}{index ? ` #${index}` : ''}</p>
          <p className="text-xs text-white/40 truncate">{doc.original_filename} — {(doc.file_size / 1024).toFixed(0)} KB</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => onDownload(doc.id, doc.original_filename)} className="p-1.5 text-white/40 hover:text-[#A38560] transition-colors"><Download size={15} /></button>
        <button onClick={() => onDelete(doc.id, doc.name)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

function DocUploadRow({ name, docType, uploading, onUpload, onDone, inputCls }) {
  const fileRef = React.useRef(null);
  return (
    <div className="flex items-center gap-3 p-3 bg-[#16302B] rounded-xl border border-white/5">
      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Upload size={16} className="text-white/30" /></div>
      <span className="text-sm text-white/60 flex-1 truncate">{name}</span>
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.webp"
        onChange={e => { if (e.target.files[0]) { onUpload(e.target.files[0], docType, name); onDone(); e.target.value = ''; }}} />
      <button onClick={() => fileRef.current?.click()} disabled={uploading === name}
        className="text-sm text-[#A38560] hover:text-[#C9A96E] transition-colors disabled:opacity-50">
        {uploading === name ? 'Subiendo...' : 'Subir archivo'}
      </button>
    </div>
  );
}
